import { KANAELE, KANAL_NAMEN, leereKanaele, type Kanal, type KanalZahlen } from "@talentkontor/domain";

/**
 * Traffic-Dashboard der Betreiber-Konsole (0330): reine Anzeige-Logik. Zwei Quellen:
 *  · Serverfunktion betrieb-traffic (Cloudflare Web Analytics und eigene Landungen): Besuche je Tag und Kanal, /start, /registrieren,
 *    getrennt nach eigenen Zugriffen (PT, US, ES) und dem Rest; Zeitraum und Vorperiode.
 *  · Datenbank public.platform_traffic: Registrierungen, Konten, Akquise je Tag, Kohorte des Zeitraums und Kacheln jetzt/vorher.
 */

export interface RumTeil { kanaele: KanalZahlen; besuche: number; start: number; registrieren: number }
export interface RumTag { tag: string; extern: RumTeil; intern: RumTeil }
export interface RumAntwort { tage: number; heute: string; von: string; vorher_von: string; stand: string; aus_cache: boolean; verlauf: RumTag[] }

export interface DbTag { tag: string; registrierungen: number; konten: number; email_gesendet: number; email_geklickt: number; dm_gesendet: number; dm_antwort: number }
export interface Kohorte { konten: number; zahlungsart: number; zahlend: number }
export interface Kachelstand { tag: string; konten: number; testphase_zahlungsart: number; zahlend: number; mrr_cents: number }
export interface DbAntwort {
  tage: number; heute: string; von: string; vorher_von: string; intern: boolean;
  verlauf: DbTag[];
  kohorte: { jetzt: Kohorte; vorher: Kohorte };
  kacheln: { jetzt: Kachelstand | null; vorher: Kachelstand | null };
  vergleich_ab: string | null;
}

/** Zeitraum des Traffic-Dashboards: 7, 30 oder 90 Tage (Cloudflare fragt höchstens 93 Tage je Abfrage ab). */
export const TRAFFIC_ZEITRAUM = [7, 30, 90] as const;
export function trafficTage(tage: number): 7 | 30 | 90 {
  return tage <= 7 ? 7 : tage <= 30 ? 30 : 90;
}

/**
 * Hinweis, wenn oben ein längerer Zeitraum gewählt ist (12 Monate), der Traffic-Teil aber höchstens 90 Tage zeigt: Cloudflare Web
 * Analytics liefert höchstens 90 Tage. Leer, wenn der gewählte Zeitraum passt.
 */
export function trafficGrenzeHinweis(gewaehlt: number, t: (de: string, en: string) => string): string | null {
  if (gewaehlt <= trafficTage(gewaehlt)) return null;
  return t(`Der Traffic-Teil zeigt die letzten 90 Tage statt ${gewaehlt === 365 ? "12 Monate" : `${gewaehlt} Tage`}: Cloudflare liefert höchstens 90 Tage.`,
    `The traffic section shows the last 90 days instead of ${gewaehlt === 365 ? "12 months" : `${gewaehlt} days`}: Cloudflare provides at most 90 days.`);
}

/** Farben je Kanal (Reihenfarben des Pakets; Sonstige gestrichelt) */
export const KANAL_FARBEN: Record<Kanal, { color: string; dashed?: boolean }> = {
  seo: { color: "var(--tk-s2)" }, ki: { color: "var(--tk-s4)" }, instagram: { color: "var(--tk-s6)" }, email: { color: "var(--tk-s5)" },
  social: { color: "var(--tk-s3)" }, direkt: { color: "var(--tk-s1)" }, sonstige: { color: "var(--tk-s3)", dashed: true },
};
export const kanalName = (k: Kanal, t: (de: string, en: string) => string) => t(...KANAL_NAMEN[k]);

/** Eigene Zugriffe nur mit dem Schalter „intern zeigen“. */
export function teilVon(tag: RumTag, intern: boolean): RumTeil {
  if (!intern) return tag.extern;
  const kanaele = leereKanaele();
  for (const k of KANAELE) kanaele[k] = (tag.extern.kanaele[k] ?? 0) + (tag.intern.kanaele[k] ?? 0);
  return { kanaele, besuche: tag.extern.besuche + tag.intern.besuche, start: tag.extern.start + tag.intern.start, registrieren: tag.extern.registrieren + tag.intern.registrieren };
}

/** Tage des Zeitraums (ab von) und der Vorperiode (davor). */
export function aufteilen<T extends { tag: string }>(verlauf: T[], von: string): { jetzt: T[]; vorher: T[] } {
  return { jetzt: verlauf.filter((d) => d.tag >= von), vorher: verlauf.filter((d) => d.tag < von) };
}
export const summe = <T,>(xs: T[], f: (x: T) => number) => xs.reduce((s, x) => s + (Number(f(x)) || 0), 0);

export type Richtung = "auf" | "ab" | "gleich" | "neu" | "keiner";
export interface Trend { richtung: Richtung; prozent: number | null; text: string }

/**
 * Veränderung gegenüber der Vorperiode. Ohne Vergleichswert „keiner“ (z. B. Bestandszahlen vor dem ersten Tagesstand),
 * von null auf mehr „neu“, sonst gerundete Prozent mit Vorzeichen (−, nicht -).
 */
export function trend(jetzt: number, vorher: number | null | undefined, t: (de: string, en: string) => string): Trend {
  if (vorher === null || vorher === undefined || !Number.isFinite(vorher)) return { richtung: "keiner", prozent: null, text: t("kein Vergleich", "no comparison") };
  if (vorher === 0) return jetzt === 0 ? { richtung: "gleich", prozent: 0, text: "±0 %" } : { richtung: "neu", prozent: null, text: t("neu", "new") };
  const p = Math.round(((jetzt - vorher) / Math.abs(vorher)) * 100);
  if (p === 0) return { richtung: "gleich", prozent: 0, text: "±0 %" };
  return { richtung: p > 0 ? "auf" : "ab", prozent: p, text: `${p > 0 ? "+" : "−"}${Math.abs(p)} %` };
}

/** Kurzes Datum für die Achse: „16.10.“ bzw. „10/16“ */
export function kurzDatum(tag: string, lang: "de" | "en" = "de"): string {
  const [, m, d] = tag.split("-");
  return lang === "en" ? `${Number(m)}/${Number(d)}` : `${d}.${m}.`;
}

export interface Kachel { key: string; titel: string; wert: number; vorher: number | null; geld?: boolean; hinweis: string }

/** Die sechs Kacheln mit Trend. Besuche und Registrierungen gegen die Vorperiode, Bestände gegen den Stand zu Beginn des Zeitraums. */
export function kacheln(opts: { rum: RumAntwort | null; db: DbAntwort; intern: boolean; t: (de: string, en: string) => string }): Kachel[] {
  const { rum, db, intern, t } = opts;
  const r = rum ? aufteilen(rum.verlauf, rum.von) : null;
  const d = aufteilen(db.verlauf, db.von);
  const rs = (xs: RumTag[], f: (x: RumTeil) => number) => summe(xs, (x) => f(teilVon(x, intern)));
  const vorherBestand = db.kacheln.vorher;
  const bestandHinweis = vorherBestand ? t(`gegenüber ${kurzDatum(vorherBestand.tag)}`, `vs. ${kurzDatum(vorherBestand.tag, "en")}`)
    : db.vergleich_ab ? t(`Vergleich ab ${kurzDatum(db.vergleich_ab)} möglich`, `comparison from ${kurzDatum(db.vergleich_ab, "en")}`) : t("noch kein Vergleich", "no comparison yet");
  const jetzt = db.kacheln.jetzt;
  return [
    { key: "besuche", titel: t("Besuche", "Visits"), wert: r ? rs(r.jetzt, (x) => x.besuche) : 0, vorher: r ? rs(r.vorher, (x) => x.besuche) : null,
      hinweis: rum ? t("Website, gegenüber Vorperiode", "website, vs. previous period") : t("Cloudflare nicht erreichbar", "Cloudflare unavailable") },
    { key: "start", titel: t("davon /start", "of which /start"), wert: r ? rs(r.jetzt, (x) => x.start) : 0, vorher: r ? rs(r.vorher, (x) => x.start) : null,
      hinweis: t("Aufrufe der Startseite für Einladungen", "views of the invitation page") },
    { key: "registrierungen", titel: t("Registrierungen", "Sign-ups"), wert: summe(d.jetzt, (x) => x.registrierungen), vorher: summe(d.vorher, (x) => x.registrierungen),
      hinweis: t("Anmeldungen, gegenüber Vorperiode", "sign-ins, vs. previous period") },
    { key: "zahlungsart", titel: t("Testkonten mit Zahlungsart", "Trials with payment method"), wert: jetzt?.testphase_zahlungsart ?? 0, vorher: vorherBestand?.testphase_zahlungsart ?? null, hinweis: bestandHinweis },
    { key: "zahlend", titel: t("Zahlende Kunden", "Paying customers"), wert: jetzt?.zahlend ?? 0, vorher: vorherBestand?.zahlend ?? null, hinweis: bestandHinweis },
    { key: "mrr", titel: t("MRR · netto", "MRR · net"), wert: jetzt?.mrr_cents ?? 0, vorher: vorherBestand?.mrr_cents ?? null, geld: true, hinweis: bestandHinweis },
  ];
}

export interface HeuteZahl { key: string; titel: string; wert: number; geld?: boolean; hinweis: string }

/**
 * Leiste „Heute“ oben in der Konsole (Inhaber 24.10.2026, Wunsch des Vertriebs): die Zahlen des heutigen Tages, darunter je ein
 * Vergleich (gestern oder die letzten sieben Tage). Ohne Cloudflare zählt der Besuch als 0 mit Hinweis.
 */
export function heuteZahlen(opts: { rum: RumAntwort | null; db: DbAntwort; intern: boolean; t: (de: string, en: string) => string; laedt?: boolean }): HeuteZahl[] {
  const { rum, db, intern, t, laedt } = opts;
  // K-2: solange Cloudflare noch lädt, „lädt …“ statt „nicht erreichbar“
  const ohneRum = laedt ? t("lädt …", "loading …") : t("Cloudflare nicht erreichbar", "Cloudflare unavailable");
  const tag = db.heute;
  const dbTage = db.verlauf.filter((x) => x.tag <= tag);
  const heute = dbTage.find((x) => x.tag === tag);
  const sieben = dbTage.slice(-7);
  const s7 = (f: (x: DbTag) => number) => summe(sieben, f);
  const rumHeute = rum?.verlauf.find((x) => x.tag === tag);
  const rumGestern = rum ? rum.verlauf.filter((x) => x.tag < tag).at(-1) : undefined;
  const zahl = (v: number) => v.toLocaleString("de-DE");
  const rumSieben = rum ? rum.verlauf.filter((x) => x.tag <= tag).slice(-7) : [];
  const rum7 = (f: (x: RumTeil) => number) => summe(rumSieben, (x) => f(teilVon(x, intern)));
  return [
    { key: "besucher", titel: t("Besucher heute", "Visitors today"), wert: rumHeute ? teilVon(rumHeute, intern).besuche : 0,
      hinweis: rum ? t(`gestern ${zahl(rumGestern ? teilVon(rumGestern, intern).besuche : 0)}`, `yesterday ${rumGestern ? teilVon(rumGestern, intern).besuche : 0}`) : ohneRum },
    { key: "mails", titel: t("Akquise-Mails", "Outreach emails"), wert: heute?.email_gesendet ?? 0, hinweis: t(`7 Tage: ${zahl(s7((x) => x.email_gesendet))}`, `7 days: ${s7((x) => x.email_gesendet)}`) },
    // Klicks aus Akquise-Mails: Ankünfte auf der Website über die Mail (Kanal „E-Mail-Akquise“). Der Rückkanal des Mailversands
    // meldet keine Klicks (email_geklickt bleibt 0), die Landung zählt die Website selbst
    { key: "klicks", titel: t("Klicks aus Mails", "Clicks from emails"), wert: rumHeute ? teilVon(rumHeute, intern).kanaele.email : 0,
      hinweis: rum ? t(`7 Tage: ${zahl(rum7((x) => x.kanaele.email))}`, `7 days: ${rum7((x) => x.kanaele.email)}`) : ohneRum },
    { key: "dms", titel: t("Instagram-DMs", "Instagram DMs"), wert: heute?.dm_gesendet ?? 0,
      hinweis: (heute?.dm_antwort ?? 0) === 1 ? t("1 Antwort heute", "1 reply today") : t(`${heute?.dm_antwort ?? 0} Antworten heute`, `${heute?.dm_antwort ?? 0} replies today`) },
    { key: "konten", titel: t("Neue Konten", "New accounts"), wert: heute?.konten ?? 0, hinweis: t(`7 Tage: ${zahl(s7((x) => x.konten))}`, `7 days: ${s7((x) => x.konten)}`) },
    { key: "mrr", titel: t("MRR · netto", "MRR · net"), wert: db.kacheln.jetzt?.mrr_cents ?? 0, geld: true,
      hinweis: (db.kacheln.jetzt?.testphase_zahlungsart ?? 0) === 1 ? t("1 Testphase mit Zahlungsart", "1 trial with payment method")
        : t(`${db.kacheln.jetzt?.testphase_zahlungsart ?? 0} Testphasen mit Zahlungsart`, `${db.kacheln.jetzt?.testphase_zahlungsart ?? 0} trials with payment method`) },
  ];
}

/** Gruß über der Konsole nach der Tageszeit (lokale Uhr). */
export function gruss(stunde: number, t: (de: string, en: string) => string): string {
  if (stunde >= 5 && stunde < 11) return t("Guten Morgen", "Good morning");
  if (stunde >= 11 && stunde < 18) return t("Guten Tag", "Good afternoon");
  return t("Guten Abend", "Good evening");
}

export interface Schritt { schritt: string; n: number; quelle: "website" | "konto" }

/** Weg vom Website-Besuch zum zahlenden Kunden im Zeitraum: Website-Stufen aus Cloudflare, ab „Konto“ die Konten des Zeitraums. */
export function websiteTrichter(opts: { rum: RumAntwort | null; db: DbAntwort; intern: boolean; t: (de: string, en: string) => string }): Schritt[] {
  const { rum, db, intern, t } = opts;
  const r = rum ? aufteilen(rum.verlauf, rum.von).jetzt : [];
  const rs = (f: (x: RumTeil) => number) => summe(r, (x) => f(teilVon(x, intern)));
  const k = db.kohorte.jetzt;
  return [
    ...(rum ? [
      { schritt: t("Website-Besuch", "Website visit"), n: rs((x) => x.besuche), quelle: "website" as const },
      { schritt: "/start", n: rs((x) => x.start), quelle: "website" as const },
      { schritt: "/registrieren", n: rs((x) => x.registrieren), quelle: "website" as const },
    ] : []),
    { schritt: t("Konto angelegt", "Account created"), n: k.konten, quelle: "konto" },
    { schritt: t("Zahlungsart hinterlegt", "Payment method added"), n: k.zahlungsart, quelle: "konto" },
    { schritt: t("Zahlender Kunde", "Paying customer"), n: k.zahlend, quelle: "konto" },
  ];
}

/**
 * Erfundene Besucherzahlen für den Demo-Modus (keine Serverfunktion): gleichmäßig schwankend, mit Wochenrhythmus, damit Kurven
 * und Kacheln etwas zeigen. Gleiche Eingabe, gleiche Zahlen.
 */
export function demoRum(tage: number, heute: string): RumAntwort {
  const tageListe: string[] = [];
  const end = Date.parse(`${heute}T00:00:00Z`);
  for (let i = 2 * tage - 1; i >= 0; i--) tageListe.push(new Date(end - i * 86_400_000).toISOString().slice(0, 10));
  let seed = 7;
  const zufall = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  const verlauf = tageListe.map((tag, i) => {
    const wochentag = new Date(`${tag}T00:00:00Z`).getUTCDay();
    const welle = wochentag === 0 || wochentag === 6 ? 0.55 : 1;
    const wachstum = 0.7 + (i / tageListe.length) * 0.6;
    const k = leereKanaele();
    const basis: KanalZahlen = { seo: 22, ki: 4, instagram: 9, email: wochentag >= 2 && wochentag <= 4 ? 12 : 3, social: 3, direkt: 14, sonstige: 2 };
    for (const key of KANAELE) k[key] = Math.round(basis[key] * welle * wachstum * (0.7 + zufall() * 0.6));
    const besuche = KANAELE.reduce((s, key) => s + k[key], 0);
    const start = Math.round(k.email * 0.9 + besuche * 0.05);
    const intern = leereKanaele();
    intern.direkt = Math.round(3 + zufall() * 4);
    return { tag, extern: { kanaele: k, besuche, start, registrieren: Math.round(start * 0.35) },
      intern: { kanaele: intern, besuche: intern.direkt, start: Math.round(zufall() * 2), registrieren: 0 } };
  });
  return { tage, heute, von: tageListe[tage] ?? heute, vorher_von: tageListe[0] ?? heute, stand: `${heute}T08:00:00.000Z`, aus_cache: false, verlauf };
}
